import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ActivityEntityName, CommandCenterSecurityRight, EntityQueueDefinition, EntityViewDisplay } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import type { EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import { buildQueueRowCommands, canEditQueueRow, openQueueEditDialog } from './commands/queue.row.commands';

const card: EntityManifestCard<EntityQueueDefinition> = {
  component: () => import('./presentation/card.entity.queue').then(module => module.CardEntityQueueComponent),
  resolution: 'snapshot',
  summarize: queue => ({ title: queue.name ?? 'Queue', line1: queue.owner?.name, line2: queue.audience }),
};

// Admin board over shared queues. Create stays in the inbox: a queue is authored where it is used,
// this screen exists to find, fix, hand over and remove queues other people made.
export const entityQueueManifest = registerManifest<EntityQueueDefinition>({
  entity: ActivityEntityName.EntityQueueDefinition,
  slug: 'queues',
  routes: {
    root: '/settings/queues',
  },
  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      header: {
        title: 'Queues',
        showSearch: true,
        showPaginator: true,
        showViewOptions: false,
        // No create here: a queue is authored in the inbox where it is used.
        hideAdd: true,
      },
      itemInteraction: {
        // The board runs navigate in an injection context; without it a row click opens the inspector.
        // A queue this user may not edit opens in the inbox instead, so the click never lands on a refused save.
        navigate: queue => {
          if (!queue._id) return;
          if (canEditQueueRow(queue)) void openQueueEditDialog(inject(EntityDialogs), queue._id);
          else void inject(Router).navigate(['/inbox', 'queue', queue._id]);
        },
        commandsDisplay: 'menu',
        commands: row => buildQueueRowCommands(row),
      },
    },
  },
  presentation: { card },
  operations: {
    edit: {
      kind: 'custom',
      load: () => import('./dialog/queue.edit.dialog').then(module => module.QueueEditDialogComponent),
      presentation: { mode: 'dialog', title: 'Edit Queue', width: 'min(480px, calc(100vw - 40px))', height: 'min(760px, calc(100vh - 48px))' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(CommandCenterSecurityRight.CanManageSharedQueues),
    },
  },
});
